import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ScoreFullscreen.css";

/**
 * Full-screen score mode for courtside scoring, around any sport scoreboard.
 *
 * - Uses the browser Fullscreen API when it can (Android/desktop) and always
 *   falls back to a fixed full-window overlay (iPhone Safari has no
 *   fullscreen for pages), so it works everywhere.
 * - Keeps the screen awake while open (Wake Lock API, where supported).
 * - On phones it tries to turn to landscape; if the browser refuses, the
 *   layout still fits portrait.
 * - Esc or the Exit button leaves it.
 */
const ScoreFullscreen = ({ children, enabled = true, label = "Full screen" }) => {
  const shellRef = useRef(null);
  const wakeLockRef = useRef(null);
  // True while the browser's own fullscreen is on for this shell
  const nativeRef = useRef(false);
  const [isFull, setIsFull] = useState(false);

  const releaseWakeLock = useCallback(() => {
    wakeLockRef.current?.release?.().catch(() => {});
    wakeLockRef.current = null;
  }, []);

  const requestWakeLock = useCallback(async () => {
    try {
      if (navigator.wakeLock?.request) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch {
      // Not allowed (battery saver, old browser): scoring works without it
    }
  }, []);

  const enter = useCallback(async () => {
    setIsFull(true);
    const el = shellRef.current;
    try {
      if (el?.requestFullscreen && document.fullscreenEnabled) {
        await el.requestFullscreen({ navigationUI: "hide" });
        nativeRef.current = true;
        await window.screen?.orientation?.lock?.("landscape").catch(() => {});
      }
    } catch {
      // The overlay alone is used
    }
    requestWakeLock();
  }, [requestWakeLock]);

  const exit = useCallback(() => {
    setIsFull(false);
    nativeRef.current = false;
    releaseWakeLock();
    try {
      window.screen?.orientation?.unlock?.();
    } catch {
      // ignore
    }
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  }, [releaseWakeLock]);

  // Leaving browser fullscreen (Esc, back gesture) also leaves score mode
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement && nativeRef.current) exit();
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [exit]);

  // Esc closes the overlay when the Fullscreen API was not used
  useEffect(() => {
    if (!isFull) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) exit();
    };
    window.addEventListener("keydown", onKey);
    document.body.classList.add("score-fullscreen-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("score-fullscreen-open");
    };
  }, [isFull, exit]);

  // The wake lock drops when the tab is hidden; take it again on return
  useEffect(() => {
    if (!isFull) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") requestWakeLock();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isFull, requestWakeLock]);

  useEffect(() => () => releaseWakeLock(), [releaseWakeLock]);

  if (!enabled) return children;

  return (
    <div ref={shellRef} className={`score-fs-shell${isFull ? " is-fullscreen" : ""}`}>
      <div className="score-fs-bar">
        {isFull ? (
          <button type="button" className="score-fs-btn score-fs-btn--exit" onClick={exit} aria-label="Exit full screen">
            ✕ Exit full screen
          </button>
        ) : (
          <button type="button" className="score-fs-btn" onClick={enter} title="Big scores for courtside scoring">
            ⛶ {label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
};

export default ScoreFullscreen;
